'use client'

import { X } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'

interface IncidentPayload {
  enabled: boolean
  message?: string
  link?: { label?: string; href?: string }
  postedAt?: string
}

const DISMISS_STORAGE_KEY = 'reviewer3.incidentBanner.dismissed'
const FLAG_KEY = 'incident_banner'

function readDismissedPostedAt(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { dismissedPostedAt?: unknown }
    return typeof parsed?.dismissedPostedAt === 'string' ? parsed.dismissedPostedAt : null
  } catch {
    return null
  }
}

function writeDismissed(postedAt: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      DISMISS_STORAGE_KEY,
      JSON.stringify({ dismissedPostedAt: postedAt, dismissedAt: Date.now() })
    )
  } catch {
    // ignore quota / private-browsing failures
  }
}

export function IncidentBanner() {
  const posthog = usePostHog()
  const pathname = usePathname()
  const [payload, setPayload] = useState<IncidentPayload | null>(null)
  const [dismissed, setDismissed] = useState<string | null>(null)

  useEffect(() => {
    setDismissed(readDismissedPostedAt())
    if (!posthog) return

    const refresh = () => {
      const raw = posthog.getFeatureFlagPayload(FLAG_KEY)
      if (!raw || typeof raw !== 'object') {
        setPayload(null)
        return
      }
      setPayload(raw as unknown as IncidentPayload)
    }

    refresh()
    posthog.reloadFeatureFlags?.()
    const unsubscribe = posthog.onFeatureFlags?.(refresh)
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe()
    }
  }, [posthog, pathname])

  if (!payload || !payload.enabled) return null
  const message = typeof payload.message === 'string' ? payload.message.trim() : ''
  if (!message) return null

  const postedAt = typeof payload.postedAt === 'string' ? payload.postedAt : ''
  if (postedAt && dismissed === postedAt) return null

  const link = payload.link
  const hasLink = link && typeof link.href === 'string' && link.href.length > 0

  const onDismiss = () => {
    if (postedAt) writeDismissed(postedAt)
    setDismissed(postedAt || null)
  }

  return (
    <div
      className="flex items-center gap-3 border-b border-yellow-300 px-4 py-1.5 text-sm font-medium text-yellow-900"
      style={{ background: 'var(--incident-banner-bg, #FEF3C7)' }}
    >
      <div className="flex flex-1 items-center justify-center gap-3">
        <span>{message}</span>
        {hasLink && (
          <a
            href={link!.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-yellow-950"
          >
            {link!.label && link!.label.length > 0 ? link!.label : link!.href}
          </a>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="rounded p-0.5 text-yellow-900/70 hover:bg-yellow-200 hover:text-yellow-900"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
